import { isCancelledError, useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { PlatformSupportErrorCode } from '@web-app/generated/platform/graphql';
import { trpc } from '@web-app/utils/trpc';
import { useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { type Confirmations, confirmationsKey } from './support-confirmations';
import { observeConversation } from './support-observed';

type RefreshResult = {
  outcome: 'completed' | 'skipped';
  refreshScope: 'conversation' | 'inbox';
  reason: string;
};
type PendingRefresh = {
  again: boolean;
  correlationId: string;
  promise: Promise<RefreshResult>;
};

/** Fetch current authorized data; webhook bodies never enter the query cache. */
export function useSupportConversationRefresh(
  viewerId: string,
  enabled: boolean,
) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const state = useMemo(
    () => ({
      viewerId,
      enabled,
      active: true,
      pending: new Map<string, PendingRefresh>(),
    }),
    [viewerId, enabled],
  );
  const current = useRef(state);
  current.current = state;
  const isCurrent = () =>
    state.enabled && state.active && current.current === state;
  useEffect(
    function cancelPreviousViewerRefreshes() {
      state.active = true;
      return () => {
        state.active = false;
        for (const id of state.pending.keys())
          void utils.supportConcierge.conversation.cancel({
            viewerId: state.viewerId,
            id,
          });
      };
    },
    [state, utils],
  );

  async function refreshOnce(
    id: string,
    correlationId: string,
  ): Promise<RefreshResult> {
    const scope = { viewerId, id };
    const inbox = utils.supportConcierge.conversations.getInfiniteData({
      viewerId,
    });
    const hasDetail = !!queryClient.getQueryState(
      getQueryKey(trpc.supportConcierge.conversation, scope, 'query'),
    );
    const hasInbox = !!queryClient.getQueryState(
      getQueryKey(
        trpc.supportConcierge.conversations,
        { viewerId },
        'infinite',
      ),
    );
    const inInbox = inbox?.pages.some((page) =>
      page.conversations.some((item) => item.id === id),
    );
    if (!hasDetail && !inInbox && !hasInbox) {
      // Off the Concierge page only notifications know conversations, and their
      // unread search lags the webhook, so read this one directly for them.
      if (!utils.supportConcierge.unreadConversations.getData({ viewerId }))
        return {
          outcome: 'skipped',
          refreshScope: 'conversation',
          reason: 'not_cached',
        };
    } else if (!hasDetail && !inInbox) {
      // Unknown membership needs authoritative pagination, not a guessed insertion.
      await utils.supportConcierge.conversations.cancel({ viewerId });
      if (!isCurrent())
        return {
          outcome: 'skipped',
          refreshScope: 'inbox',
          reason: 'scope_ended',
        };
      await Promise.all([
        utils.supportConcierge.conversations.invalidate(
          { viewerId },
          undefined,
          { throwOnError: true },
        ),
        utils.supportConcierge.unreadConversations.invalidate({ viewerId }),
      ]);
      return {
        outcome: 'completed',
        refreshScope: 'inbox',
        reason: 'unknown_conversation',
      };
    }
    // Do not join a read that began before this hint.
    await utils.supportConcierge.conversation.cancel(scope);
    if (!isCurrent())
      return {
        outcome: 'skipped',
        refreshScope: 'conversation',
        reason: 'scope_ended',
      };
    const options = utils.supportConcierge.conversation.queryOptions(scope, {
      staleTime: 0,
      gcTime: 0,
      retry: false,
      trpc: {
        abortOnUnmount: true,
        context: { supportCorrelationId: correlationId },
      },
    });
    // Stage each read outside the displayed query until its result is validated.
    // A child key keeps viewer/conversation cancellation working. A fresh key
    // also prevents cancellation from resolving with a previous staging result.
    const requestedAt = Date.now();
    const result = await queryClient.fetchQuery({
      ...options,
      queryKey: [
        [...options.queryKey[0], 'refresh', uuid()],
        options.queryKey[1],
      ],
    });
    if (!isCurrent())
      return {
        outcome: 'skipped',
        refreshScope: 'conversation',
        reason: 'scope_ended',
      };
    const removed = result.error?.code === PlatformSupportErrorCode.NotFound;
    if (
      !removed &&
      (result.error || !result.conversation || result.conversation.id !== id)
    ) {
      throw new Error('Support conversation refresh was not confirmed.');
    }
    // A notifications-only refresh owns no page reads: any that started since
    // are newer first loads, and cancelling them would leave the page loading.
    if (hasDetail || inInbox) {
      // Stop concurrent reads before committing the validated detail and inbox row.
      await Promise.all([
        utils.supportConcierge.conversation.cancel(scope),
        utils.supportConcierge.conversations.cancel({ viewerId }),
      ]);
      if (!isCurrent())
        return {
          outcome: 'skipped',
          refreshScope: 'conversation',
          reason: 'scope_ended',
        };
      utils.supportConcierge.conversation.setData(scope, result);
    }
    utils.supportConcierge.conversations.setInfiniteData(
      { viewerId },
      (previous) =>
        previous && {
          ...previous,
          // Preserve page boundaries and cursors; the view sorts the flattened rows.
          pages: previous.pages.map((page) => ({
            ...page,
            conversations: page.conversations.flatMap((item) =>
              item.id !== id
                ? [item]
                : removed || !result.conversation
                  ? []
                  : [result.conversation],
            ),
          })),
        },
    );
    observeConversation(
      queryClient,
      viewerId,
      id,
      result.conversation ?? null,
      requestedAt,
    );
    if (removed) {
      // Confirmed local writes must not resurrect a revoked/deleted conversation.
      queryClient.setQueryData<Confirmations>(
        confirmationsKey(viewerId),
        (previous) => {
          if (!previous) return previous;
          const replies = { ...previous.replies };
          delete replies[id];
          return {
            created: previous.created.filter((item) => item.id !== id),
            replies,
          };
        },
      );
    }
    return {
      outcome: 'completed',
      refreshScope: 'conversation',
      reason: removed ? 'conversation_removed' : 'conversation_refreshed',
    };
  }

  return function refreshConversation(
    id: string,
    correlationId: string,
  ): Promise<RefreshResult> {
    if (!isCurrent())
      return Promise.resolve({
        outcome: 'skipped',
        refreshScope: 'conversation',
        reason: 'scope_ended',
      });
    const pending = state.pending.get(id);
    if (pending) {
      pending.again = true;
      pending.correlationId = correlationId;
      return pending.promise;
    }
    const job: PendingRefresh = {
      again: false,
      correlationId,
      promise: Promise.resolve({
        outcome: 'skipped',
        refreshScope: 'conversation',
        reason: 'not_cached',
      }),
    };
    state.pending.set(id, job);
    job.promise = (async () => {
      while (isCurrent()) {
        job.again = false;
        try {
          const result = await refreshOnce(id, job.correlationId);
          if (!job.again) return result;
        } catch (error) {
          if (!isCurrent() || isCancelledError(error))
            return {
              outcome: 'skipped',
              refreshScope: 'conversation',
              reason: 'superseded',
            } as const;
          if (!job.again) throw error;
        }
      }
      return {
        outcome: 'skipped',
        refreshScope: 'conversation',
        reason: 'scope_ended',
      } as const;
    })().finally(() => state.pending.delete(id));
    return job.promise;
  };
}
